'use client'
import { memo, useState, useEffect } from 'react'

import { useRouter, usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { Pencil, Smartphone, Mail } from 'lucide-react'
import { User } from '@/types/api'
import { formatPhoneNumber } from '@/utils/phone'
import { Spinner } from '@/components/ui/Loading'

interface AccountSectionProps {
  user: User | null
  onNavigate?: () => void
}

const MAX_EMAIL_LENGTH = 20

const truncateEmail = (email: string | null | undefined): string => {
  if (!email) return ''
  return email.length > MAX_EMAIL_LENGTH
    ? `${email.slice(0, MAX_EMAIL_LENGTH)}...`
    : email
}

export default memo(function AccountSection({
  user,
  onNavigate,
}: AccountSectionProps) {
  const router = useRouter()
  const pathname = usePathname()
  const t = useTranslations('navigation')
  const [isLoading, setIsLoading] = useState(false)

  const isAccountPage = pathname === '/account'

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset loading state once route change completes
    setIsLoading(false)
  }, [pathname])

  const handleAccountClick = () => {
    if (isAccountPage || isLoading) return
    setIsLoading(true)
    router.push('/account')
    onNavigate?.()
  }

  return (
    <div
      className={`w-full px-3 py-4 border-b border-border shrink-0 ${
        isAccountPage ? 'bg-muted' : ''
      }`}
    >
      <button
        onClick={handleAccountClick}
        disabled={isLoading || isAccountPage}
        className="w-full text-left cursor-pointer hover:opacity-80 transition-opacity duration-150 group touch-manipulation disabled:cursor-default"
      >
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-2">
            {t('account')}
          </h2>
          <div
            className={`p-1 rounded-md transition-[color,transform] duration-150 ${
              isAccountPage
                ? 'text-primary scale-110 rotate-12'
                : 'text-muted-foreground md:group-hover:text-primary md:group-hover:scale-110 md:group-hover:rotate-12'
            }`}
          >
            {isLoading ? (
              <Spinner size="sm" />
            ) : (
              <Pencil className="w-3.5 h-3.5" />
            )}
          </div>
        </div>
      </button>
      <div className="space-y-0.5">
        {/* Phone */}
        <button
          onClick={handleAccountClick}
          disabled={isLoading || isAccountPage}
          className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-muted active:bg-muted/80 transition-colors duration-150 cursor-pointer touch-manipulation disabled:pointer-events-none"
        >
          <Smartphone className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="text-sm font-medium text-foreground">
            {user?.phone ? formatPhoneNumber(user.phone) : t('noPhone')}
          </span>
        </button>

        {/* Email */}
        <button
          onClick={handleAccountClick}
          disabled={isLoading || isAccountPage}
          className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-muted active:bg-muted/80 transition-colors duration-150 cursor-pointer touch-manipulation disabled:pointer-events-none"
        >
          <Mail className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="text-sm font-medium text-foreground truncate">
            {truncateEmail(user?.email) || t('noEmail')}
          </span>
        </button>
      </div>
    </div>
  )
})
